import type React from "react";
import { type ComponentProps, memo, useCallback, useMemo, useRef } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";

import type { ToolCallEntry } from "@/api/ai/types";
import {
  EDITOR_FONT,
  editorRegionFocusRing,
  JsonViewer,
} from "@/components/JsonViewer";
import { OverlayScroll } from "@/components/OverlayScroll";
import { useNearViewport } from "@/hooks/useNearViewport";
import {
  rhaiHighlightStyle,
  rhaiLanguage,
} from "@/pages/NewStudio/rhaiLanguage";
import { ChartBlock } from "./ChartBlock";
import { CHART_FENCE_ARTIFACT } from "./chartFence";
import { StudioActionsBlock } from "./StudioActionsBlock";
import { useStudioActionsHost } from "./StudioActionsContext";
import { STUDIO_FENCE_ARTIFACT } from "./studioActionFence";
import { ToolCallBadge } from "./ToolCallBadge";

interface ChatResponseProps {
  children: string;
  /** The message's id — Studio action rows key their results on it. */
  messageId: string;
  onApplyCode?: (
    code: string,
    lang: string,
    artifact: string | null,
    name: string | null,
  ) => void;
  toolRounds?: number;
  toolCalls?: ToolCallEntry[];
}

export const ChatResponse: React.FC<ChatResponseProps> = ({
  children,
  messageId,
  onApplyCode,
  toolRounds,
  toolCalls,
}) => {
  // Read here, not in the memoized body: the host value changes with every
  // Studio state change (its rows describe live state), and a context
  // consumer re-renders on that regardless of memo — the body only needs
  // to know whether a host exists.
  const hasHost = useStudioActionsHost() !== null;
  return (
    <Container>
      {toolCalls != null && toolCalls.length > 0 ? (
        <ToolCallList>
          {toolCalls.map((tc) => (
            <ToolCallBadge key={tc.id} entry={tc} />
          ))}
        </ToolCallList>
      ) : toolRounds != null && toolRounds > 0 ? (
        <ToolBadge>
          🔧 Used {toolRounds} tool {toolRounds === 1 ? "call" : "calls"}
        </ToolBadge>
      ) : null}
      <ResponseContent>
        <MarkdownBody
          content={children}
          messageId={messageId}
          onApplyCode={onApplyCode}
          hasHost={hasHost}
        />
      </ResponseContent>
    </Container>
  );
};

// Memoized: a conversation holds many messages, each a markdown parse
// with CodeMirror viewers inside — an unchanged message must not re-render
// when the sidebar does (the Studio host re-renders it on every state
// change; the Insert handler keeps one identity for that reason).
const MarkdownBody = memo(function MarkdownBody({
  content,
  messageId,
  onApplyCode,
  hasHost,
}: {
  content: string;
  messageId: string;
  onApplyCode?: (
    code: string,
    lang: string,
    artifact: string | null,
    name: string | null,
  ) => void;
  hasHost: boolean;
}) {
  // The Insert handler changes identity with the Studio's state (its
  // routing reads the selection); it reaches the fence components through
  // a ref so the component map below keeps ITS identity. A new map is a
  // new component type per markdown node — react-markdown would remount
  // every code block, and with them every CodeMirror viewer in the
  // conversation, on each tab switch or keystroke in the workbench.
  const applyRef = useRef(onApplyCode);
  applyRef.current = onApplyCode;
  const handleApply = useCallback(
    (
      code: string,
      lang: string,
      artifact: string | null,
      name: string | null,
    ) => applyRef.current?.(code, lang, artifact, name),
    [],
  );

  const showApply = onApplyCode !== undefined;
  const components = useMemo(
    () => buildComponents(handleApply, showApply, messageId, hasHost),
    [handleApply, showApply, messageId, hasHost],
  );

  return (
    <Markdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </Markdown>
  );
});

/**
 * Parse code fence info string for artifact metadata.
 *
 * Convention: ```lang:type:name
 *   - lang:script:mask_pii.rhai  → artifact (show Insert)
 *   - lang:schema:pii_masked     → artifact (show Insert)
 *   - lang:input:sample          → artifact (show Insert)
 *   - lang:pipeline              → artifact (show Insert)
 *   - lang (no marker)           → plain result (no Insert)
 */
function parseCodeFence(raw: string): {
  lang: string;
  artifact: string | null;
  name: string | null;
} {
  const parts = raw.split(":");
  const lang = parts[0] || "code";
  const artifact = parts[1] || null;
  const name = parts[2] || null;
  return { lang, artifact, name };
}

const ARTIFACT_TYPES = new Set([
  "script",
  "schema",
  "input",
  "pipeline",
  "tests",
]);

type FenceElement = React.ReactElement<{
  className?: string;
  children?: React.ReactNode;
}>;

// The fenced block a markdown <pre> wraps: its info string parsed, its
// text extracted; null when the child isn't a code element.
function codeFenceOf(children: React.ReactNode) {
  const child = Array.isArray(children) ? children[0] : children;
  const isElement =
    child != null && typeof child === "object" && "props" in child;
  if (!isElement) return null;
  const props = (child as FenceElement).props;
  const infoString = (props.className ?? "").replace("language-", "") || "code";
  return {
    ...parseCodeFence(infoString),
    code: extractCodeText(props.children),
  };
}

// Whether a fence is an artifact the Studio can insert (the button shows
// for those only; a plain fence is just code).
function insertable(artifact: string | null): boolean {
  return artifact !== null && ARTIFACT_TYPES.has(artifact);
}

function extractCodeText(children: React.ReactNode): string {
  const raw = Array.isArray(children) ? children.join("") : String(children);
  return raw.replace(/\n$/, "");
}

function buildComponents(
  onApply: (
    code: string,
    lang: string,
    artifact: string | null,
    name: string | null,
  ) => void,
  showApply: boolean,
  messageId: string,
  hasHost: boolean,
) {
  return {
    h1: ({ children }: { children?: React.ReactNode }) => (
      <Heading1>{children}</Heading1>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <Heading2>{children}</Heading2>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <Heading3>{children}</Heading3>
    ),
    p: ({ children }: { children?: React.ReactNode }) => (
      <Paragraph>{children}</Paragraph>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <ListItem>{children}</ListItem>
    ),
    strong: ({ children }: { children?: React.ReactNode }) => (
      <Bold>{children}</Bold>
    ),
    em: ({ children }: { children?: React.ReactNode }) => (
      <Italic>{children}</Italic>
    ),
    code: ({
      children,
      className,
    }: {
      children?: React.ReactNode;
      className?: string;
    }) => {
      if (className) return <code className={className}>{children}</code>;
      return <InlineCode>{children}</InlineCode>;
    },
    pre: ({ children }: { children?: React.ReactNode }) => {
      const fence = codeFenceOf(children);
      if (!fence) return <pre>{children}</pre>;
      // Only a Studio host turns a studio fence into action rows; elsewhere
      // (the Agent page) it is code like any other.
      if (fence.artifact === STUDIO_FENCE_ARTIFACT && hasHost) {
        return <StudioActionsBlock code={fence.code} messageId={messageId} />;
      }
      // A chart needs no host: it draws on the Agent page and in the
      // Studio sidebar alike, and falls back to the code with a note.
      if (fence.artifact === CHART_FENCE_ARTIFACT) {
        return (
          <ChartBlock
            code={fence.code}
            fallback={(note) => (
              <CodeBlock>
                <CodeHeader>
                  <CodeLanguage>chart · {note}</CodeLanguage>
                </CodeHeader>
                <LazyCodeViewer code={fence.code} lang={fence.lang} />
              </CodeBlock>
            )}
          />
        );
      }
      return (
        <CodeBlock>
          <CodeHeader>
            <CodeLanguage>{fence.name ?? fence.lang}</CodeLanguage>
            {showApply && insertable(fence.artifact) && (
              <InsertButton
                onClick={() =>
                  onApply(fence.code, fence.lang, fence.artifact, fence.name)
                }
              >
                Insert
              </InsertButton>
            )}
          </CodeHeader>
          <LazyCodeViewer code={fence.code} lang={fence.lang} />
        </CodeBlock>
      );
    },
    table: ({ children }: { children?: React.ReactNode }) => (
      <Table>{children}</Table>
    ),
    th: ({ children }: { children?: React.ReactNode }) => <Th>{children}</Th>,
    td: ({ children }: { children?: React.ReactNode }) => <Td>{children}</Td>,
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <Link href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </Link>
    ),
    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <BlockQuote>{children}</BlockQuote>
    ),
  };
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(p) => p.theme.semanticTextBody};
  gap: 8px;
`;

const ToolBadge = styled.div`
  font-size: 11px;
  color: ${(p) => p.theme.semanticTextLight};
  padding: 0 12px;
`;

const ToolCallList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 12px;
`;

const ResponseContent = styled.div`
  padding: 0 12px;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
`;

const Paragraph = styled.p`
  margin: 0 0 8px 0;
`;

const Heading1 = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin: 16px 0 8px 0;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const Heading2 = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 14px 0 6px 0;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const Heading3 = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 12px 0 4px 0;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const ListItem = styled.li`
  margin-left: 16px;
  margin-bottom: 4px;
  list-style-position: outside;
`;

const Bold = styled.strong`
  font-weight: 600;
`;

const Italic = styled.em`
  font-style: italic;
`;

const InlineCode = styled.code`
  background: ${(p) => p.theme.editorBackground};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 4px;
  padding: 1px 5px;
  font-family: "JetBrains Mono", "Fira Code", "Monaco", "Consolas", monospace;
  font-size: 12px;
`;

const CodeBlock = styled.div`
  background: ${(p) => p.theme.editorBackground};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 6px;
  margin: 12px 0;
  overflow: hidden;
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: ${(p) => p.theme.tableTheadCellBackground};
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
`;

// A long fence name (a file path, a wordy artifact name) truncates with
// an ellipsis; the Insert button keeps its full width beside it.
const CodeLanguage = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 500;
  color: ${(p) => p.theme.semanticTextLight};
  text-transform: uppercase;
`;

const InsertButton = styled.button`
  flex-shrink: 0;
  padding: 4px 10px;
  background: ${(p) => p.theme.buttonSecondaryHoverBackground};
  border: 1px solid ${(p) => p.theme.buttonSecondaryHoverBorder};
  border-radius: 4px;
  color: ${(p) => p.theme.buttonSecondaryIcon};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.1s ease;

  &:hover {
    background: ${(p) => p.theme.buttonSecondaryHoverBackground};
  }
`;

// Every fence reads as code does elsewhere in the app: the shared
// read-only CodeMirror viewer — line numbers, folding, the editor palette
// — speaking the fence's language. It mounts only once the block comes
// near the viewport: a conversation holds dozens of fences, and building
// every viewer at once cost the Studio page ~150ms on mount with the
// helper open — enough to stall the rail's page-switch animation. Until
// then a plain block in the editor font holds the text at about the same
// height, so the chat's scroll position barely moves on the swap.
function LazyCodeViewer({ code, lang }: { code: string; lang: string }) {
  const [ref, near] = useNearViewport<HTMLDivElement>();
  return (
    <div ref={ref}>
      <CodeViewer>
        {near ? (
          <JsonViewer value={code} {...viewerLanguage(lang)} />
        ) : (
          <CodePlaceholder>{code}</CodePlaceholder>
        )}
      </CodeViewer>
    </div>
  );
}

// The viewer's language for a fence: Rhai for scripts, the viewer's own
// JSON for JSON and Avro (the default), plain text with the same chrome
// for anything else — a JSON grammar would mis-colour YAML or shell.
function viewerLanguage(
  lang: string,
): Pick<ComponentProps<typeof JsonViewer>, "language" | "highlight"> {
  if (lang === "rhai") {
    return { language: rhaiLanguage, highlight: rhaiHighlightStyle };
  }
  return JSON_LANGS.has(lang) ? {} : { language: [] };
}

// Fence languages the viewer's default JSON grammar fits ("code" is the
// unmarked fence, JSON more often than not).
const JSON_LANGS: ReadonlySet<string> = new Set(["json", "avro", "code"]);

// Same cap as the service detail's script cards: long code scrolls
// inside the viewer instead of stretching the message.
// The cap is an OverlayScrollbars region (the app's themed scrollbars, as
// the Raw configuration modal's body): the viewer grows to its content
// and the region scrolls it, so CodeMirror's own scroller never shows a
// native bar. The keyboard focus ring is drawn over this region, the box
// the eye sees — the editor's own ring sits on its grown frame, whose
// bottom stroke was 300px of scrolling away (see editorRegionFocusRing).
const CodeViewer = styled(OverlayScroll)`
  max-height: 300px;
  ${editorRegionFocusRing}
`;

// The viewer's metrics (font, 24px rows, its padding and gutter width) so
// the block keeps its height when the viewer takes over.
const CodePlaceholder = styled.pre`
  margin: 0;
  padding: 5px 12px 10px 67px;
  font-family: ${EDITOR_FONT};
  font-size: 14px;
  line-height: 24px;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${(p) => p.theme.editorText};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 12px 0;
  font-size: 12px;
`;

const Th = styled.th`
  text-align: left;
  padding: 6px 10px;
  font-weight: 600;
  border-bottom: 2px solid ${(p) => p.theme.cards1Border};
  color: ${(p) => p.theme.semanticTextHeading};
`;

const Td = styled.td`
  padding: 6px 10px;
  border-bottom: 1px solid ${(p) => p.theme.cards1Border};
`;

// The theme's link colours: the markdown renderer autolinks emails and
// URLs in prose, and they must read against the panel (they used to take
// the primary button's near-white hover background as their colour).
const Link = styled.a`
  color: ${(p) => p.theme.linkColor};
  text-decoration: none;
  &:hover {
    color: ${(p) => p.theme.linkColorHover};
    text-decoration: underline;
  }
`;

const BlockQuote = styled.blockquote`
  margin: 8px 0;
  padding: 4px 12px;
  border-left: 3px solid ${(p) => p.theme.cards1Border};
  color: ${(p) => p.theme.semanticTextLight};
`;

export default ChatResponse;
